import { RULES } from '@dofleini/ecommerce-common';
import find from 'lodash/find';
import set from 'lodash/set';
import PropTypes from 'prop-types';
import React, { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import EntityDetailContent from '@/components/EntityDetailContent';
import CountryField from '@/components/Location/CountryField';
import createClearRenderView from '@/utils/createClearRenderView';
import { mapRelatedFields } from '@/utils/createRelatedField';

import DynamicFieldAddressLocation, { CU } from './DynamicFieldAddressLocation';

export const VALUES = {
  CUBA: 'cuba',
  OTHER: 'other',
};

const DynamicFieldAddress = ({ fieldConfig, form, viewMode }) => {
  const { t } = useTranslation('actors');
  
  const { key, label, placeholder, required } = useMemo(
    () => fieldConfig?.field || {},
    [fieldConfig],
  );
  
  const { addressType, provinces, municipalities } = useMemo(
    () => find(fieldConfig?.rules, { rule: RULES.ADDRESS })?.meta || {},
    [fieldConfig],
  );
  
  const isCU = addressType === VALUES.CUBA;
  
  useEffect(() => {
    if (isCU) {
      const values = form.getFieldsValue(true);
      set(values, `${key}.country`, CU);
      form.setFieldsValue({
        ...values,
      });
    }
  }, [form, isCU, key]);
  
  const rules = useMemo(() => required ? [{ whitespace: true }] : [], [required]);
  
  const locationProps = useMemo(() => ({
    provinces,
    municipalities,
    isCU,
    form,
    keyPrefix: key,
    viewMode
  }), [provinces, municipalities, isCU, form, key, viewMode]);
  
  const fields = useMemo(() => mapRelatedFields([
    ...(
      isCU ? [
        {
          key,
          required,
          noStyle: true,
          widget: DynamicFieldAddressLocation,
          widgetProps: locationProps
        },
      ] : [
        {
          key: `${key}.country`,
          label: t('country'),
          required,
          widget: CountryField,
        },
        {
          key,
          required,
          noStyle: true,
          widgetProps: locationProps,
          widget: DynamicFieldAddressLocation,
          related: `${key}.country`,
        },
      ]
    ),
    {
      key: `${key}.address`,
      label: label,
      required,
      widget: 'textarea',
      rules,
      widgetProps: { rows: 4, placeholder },
      renderView: createClearRenderView()
    },
  ]), [isCU, key, required, locationProps, t, label, rules, placeholder]);
  
  return (
    <EntityDetailContent form={form} fields={fields} columns={1} viewMode={viewMode}/>
  );
};

DynamicFieldAddress.propTypes = {
  fieldConfig: PropTypes.object,
  viewMode: PropTypes.bool,
  form: PropTypes.object,
};

export const renderDynamicFieldAddress = ({ field, rules, form, viewMode }) => {
  return <DynamicFieldAddress fieldConfig={{ field, rules }} form={form} viewMode={viewMode}/>;
};
